/**
 * Pricing Policy v2 — precedence and stacking rules, in one place.
 *
 * PRECEDENCE (highest first)
 *   1. campaign      — a time-boxed offer for everyone in the window
 *   2. scholarship   — equity and need-based pricing
 *   3. coupon        — a code the buyer typed
 *   4. partner       — school / corporate / NGO / reseller rate
 *   5. institution   — a negotiated per-seat institutional rate
 *   6. regional      — a region-level adjustment
 *
 * STACKING
 *   Disabled. Exactly ONE discount layer applies to an order. When two rules
 *   both resolve, the higher-precedence layer wins and the loser is recorded in
 *   `appliedRules` with `applied: false` and a reason, so the decision is
 *   auditable rather than silent.
 *
 * BASE
 *   Every discount is computed against the region list price (MRP), never
 *   against an already-discounted amount. Two 20% rules can therefore never
 *   compound into 36%.
 *
 * TIES
 *   Same layer, two candidates: the LARGER discount for the buyer wins. Equal
 *   discounts break on `id` codepoint order, so the outcome is deterministic.
 *
 * FLOOR
 *   A discount can never exceed the list price, and the payable amount can
 *   never go below zero.
 *
 * ZERO
 *   A zero payable amount is only permitted for codes in
 *   `FULL_DISCOUNT_ALLOWLIST`. Everything else is rejected as retired.
 */

import type { DiscountLayer, DiscountRule } from "./pricing-types";

/** Precedence order. Index 0 wins. */
export const LAYER_PRECEDENCE: DiscountLayer[] = [
  "campaign",
  "scholarship",
  "coupon",
  "partner",
  "institution",
  "regional",
];

/** Platform-wide: coupon stacking is off. */
export const STACKING_ENABLED = false;

/** Human-readable precedence, rendered in the admin hub. */
export const POLICY_SUMMARY = [
  "One discount per order — stacking is disabled.",
  "Precedence: campaign → scholarship → coupon → partner → institution → regional.",
  "Every discount is taken off the region MRP, never off a discounted price.",
  "Same-layer ties resolve to the larger saving, then by code order.",
  "Zero-payable orders require an allow-listed outreach code.",
];

export function layerRank(layer: DiscountLayer): number {
  const i = LAYER_PRECEDENCE.indexOf(layer);
  return i === -1 ? LAYER_PRECEDENCE.length : i;
}

/**
 * Pick the winning candidate under Pricing Policy v2. Returns the winner plus
 * every loser with the reason it lost.
 */
export function selectWinner(
  candidates: Array<{ rule: DiscountRule; discount: number }>,
): {
  winner: { rule: DiscountRule; discount: number } | null;
  losers: Array<{ rule: DiscountRule; discount: number; reason: string }>;
} {
  if (candidates.length === 0) return { winner: null, losers: [] };

  const sorted = [...candidates].sort((a, b) => {
    const byLayer = layerRank(a.rule.layer) - layerRank(b.rule.layer);
    if (byLayer !== 0) return byLayer;
    if (b.discount !== a.discount) return b.discount - a.discount;
    return a.rule.id < b.rule.id ? -1 : a.rule.id > b.rule.id ? 1 : 0;
  });

  const [winner, ...rest] = sorted;
  return {
    winner,
    losers: rest.map((c) => ({
      ...c,
      reason:
        layerRank(c.rule.layer) === layerRank(winner.rule.layer)
          ? `superseded by ${winner.rule.id} (larger saving in the same layer)`
          : `superseded by higher-precedence ${winner.rule.layer} rule ${winner.rule.id}`,
    })),
  };
}
